import React, { useState, useEffect } from "react";
import { useIntl } from "react-intl";
import { useDispatch } from "react-redux";
import { object, string, number } from "yup";
import { Form, Button, Row, Space } from "@masterteam/mt-design-react";

import AdminFormModal from "../../AdminFormModal";
import ColorPicker from "../../ColorPicker";
import Upload from "../../Upload";

import api from "../../../providers/api";
import { addToast } from "../../../helpers";
import { updatePillar } from "../../../reducers/pillar";

import "./style.scss";

const { Field } = Form;

const validationSchema = object().shape({
  EnglishName: string().required(" "),
  ArabicName: string().required(" "),
  EnglishDescription: string().required(" "),
  ArabicDescription: string().required(" "),
  Strategy: object().required(" ").nullable(),
  Order: number().required(" ").min(1, " "),
  Color: string().required(" "),
  Attachment: object().required(" ").nullable(),
});

const PillarForm = ({ data, selectedStrategy, closeModal }) => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const isAr = intl.locale === "ar";
  const [loading, setLoading] = useState(false);
  const [strategiesLoading, setStrategiesLoading] = useState(false);
  const [strategies, setStrategies] = useState([]);
  const [initialValues, setInitialValues] = useState({
    EnglishName: "",
    ArabicName: "",
    EnglishDescription: "",
    ArabicDescription: "",
    Order: "",
    Color: "",
    Strategy: selectedStrategy || null,
    Attachment: null,
  });

  const getStrategiesData = () => {
    setStrategiesLoading(true);
    api.strategy
      .getAllStrategyByType({ StrategyType: 2 })
      .then((res) => {
        setStrategies(res.Data);
      })
      .finally(() => {
        setStrategiesLoading(false);
      });
  };

  const getPillarData = () => {
    setLoading(true);
    api.pillar
      .getById(data?.ID)
      .then((res) => {
        setInitialValues(res.Data);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    getStrategiesData();
    if (data) {
      getPillarData();
    }
  }, []);

  const onSubmit = async (values, { setSubmitting }) => {
    try {
      const body = {
        ...values,
      };

      if (body.Attachment && !body.Attachment?.ID) {
        const attachments = await api.attachment.create(values.Attachment);
        body.Attachment = { ID: attachments.Data?.[0] };
      }

      const service = data ? "update" : "create";
      const res = await api.pillar[service](body);
      const { Data, StatusCode } = res;
      if (StatusCode === "Success") {
        dispatch(updatePillar(Data));
        addToast.success(data ? "toast.successUpdate" : "toast.successSave");
        closeModal();
      }
    } catch (error) {
      // addToast.error(error?.message, {
      //   toastId: "get-users-error",
      // });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AdminFormModal
      title={intl.formatMessage({
        id: data ? "common.update-pillar" : "common.add-pillar",
      })}
      isLoading={loading}
      onClose={closeModal}
    >
      <Form
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
      >
        {({ values, errors, isSubmitting, isValid, setFieldValue }) => (
          <>
            <Space
              direction="vertical"
              size="large"
              className="pillar-form-body"
            >
              <Space direction="vertical" className="form-container">
                <Row size="small">
                  <Field
                    name="ArabicName"
                    label={intl.formatMessage({
                      id: "pillar.arabic-title",
                    })}
                    inputProps={{
                      maxLength: 256,
                      placeholder: intl.formatMessage({
                        id: "pillar.arabic-title",
                      }),
                    }}
                    required
                  />
                  <Field
                    name="EnglishName"
                    label={intl.formatMessage({
                      id: "pillar.english-title",
                    })}
                    inputProps={{
                      maxLength: 256,
                      placeholder: intl.formatMessage({
                        id: "pillar.english-title",
                      }),
                    }}
                    required
                  />
                </Row>

                <Row size="small">
                  <Field
                    type="textarea"
                    name="ArabicDescription"
                    label={intl.formatMessage({
                      id: "pillar.arabic-description",
                    })}
                    inputProps={{
                      maxLength: 150,
                      placeholder: intl.formatMessage({
                        id: "pillar.arabic-description",
                      }),
                    }}
                    required
                  />

                  <Field
                    type="textarea"
                    name="EnglishDescription"
                    label={intl.formatMessage({
                      id: "pillar.english-description",
                    })}
                    inputProps={{
                      maxLength: 150,
                      placeholder: intl.formatMessage({
                        id: "pillar.english-description",
                      }),
                    }}
                    required
                  />
                </Row>

                <Row size="small">
                  <Field
                    type="select"
                    name="Strategy"
                    label={intl.formatMessage({
                      id: "common.strategy",
                    })}
                    inputProps={{
                      options: strategies,
                      getOptionValue: (item) => item.ID,
                      getOptionLabel: (item) =>
                        isAr ? item.ArabicName : item.EnglishName,
                      placeholder: intl.formatMessage({
                        id: "common.strategy",
                      }),
                      isLoading: strategiesLoading,
                      maxMenuHeight: "165px",
                    }}
                    required
                    disabled={data}
                  />
                  <Field
                    type="number"
                    name="Order"
                    label={intl.formatMessage({
                      id: "common.order",
                    })}
                    inputProps={{
                      onKeyDown: (evt) => {
                        if (
                          evt.key === "e" ||
                          evt.key === "-" ||
                          evt.key === "+"
                        ) {
                          evt.preventDefault();
                        }
                      },
                      placeholder: intl.formatMessage({
                        id: "common.order",
                      }),
                    }}
                    required
                  />
                </Row>

                <Row size="small">
                  <Space className="icon-wrapper">
                    <Space gap="3px">
                      {intl.formatMessage({ id: "common.icon" })}
                      <span className="asterisk">*</span>
                    </Space>
                    <Upload
                      name="Attachment"
                      error={errors.Attachment}
                      onChange={(file) => setFieldValue("Attachment", file)}
                      onDelete={() => setFieldValue("Attachment", null)}
                      attachments={values.Attachment}
                      accept={{
                        "image/svg+xml": [".svg"],
                      }}
                    />
                  </Space>

                  <Space direction="vertical" className="field-wrapper">
                    <Space className="field-text-label" gap="3px">
                      {intl.formatMessage({ id: "pillar.color" })}
                      <span className="asterisk">*</span>
                    </Space>
                    <ColorPicker name="Color" />
                  </Space>
                </Row>
              </Space>

              <Row justify="end" className="form-footer">
                <Button
                  loading={isSubmitting}
                  disabled={!isValid}
                  variant="primary"
                  type="submit"
                  fixed
                >
                  {intl.formatMessage({
                    id: data ? "common.update" : "common.add",
                  })}
                </Button>
              </Row>
            </Space>
          </>
        )}
      </Form>
    </AdminFormModal>
  );
};

export default PillarForm;
